import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, CalendarClock } from "lucide-react";

// Partial Delivery for one Transfer Order line: ticking the option opens an
// editable list of lots — required delivery date + qty — that must add up
// to the line qty (the backend enforces the same rule, see
// _normalize_delivery_schedules). Lot dates are kept as "YYYY-MM-DD" in the
// form; toPartialPayload() turns them into the API shape.

const num = (v) => Number(v || 0);
const round = (v) => Math.round(v * 1000) / 1000;

export const scheduleTotal = (line) => round((line.delivery_schedule || []).reduce((a, e) => a + num(e.qty), 0));

// Form state from a saved line (dates trimmed to the date input's format).
export const partialFromLine = (l) => ({
  partial_delivery: !!l.partial_delivery,
  delivery_schedule: (l.delivery_schedule || []).map((e) => ({
    delivery_date: e.delivery_date ? String(e.delivery_date).slice(0, 10) : "", qty: e.qty,
  })),
});

// API fields for a line on submit.
export const toPartialPayload = (l) => (l.partial_delivery
  ? {
    partial_delivery: true,
    delivery_schedule: (l.delivery_schedule || []).map((e) => ({
      delivery_date: e.delivery_date ? new Date(e.delivery_date).toISOString() : null, qty: num(e.qty),
    })),
  }
  : { partial_delivery: false, delivery_schedule: [] });

// First problem with a line's lots, or "" — for a toast before submitting.
export const partialDeliveryError = (l, idx) => {
  if (!l.partial_delivery) return "";
  const lots = l.delivery_schedule || [];
  if (!lots.length) return `Line ${idx + 1}: add at least one delivery date and qty`;
  if (lots.some((e) => !e.delivery_date)) return `Line ${idx + 1}: every partial delivery needs a date`;
  if (lots.some((e) => num(e.qty) <= 0)) return `Line ${idx + 1}: every partial delivery qty must be greater than 0`;
  if (Math.abs(scheduleTotal(l) - num(l.qty)) > 1e-6) {
    return `Line ${idx + 1}: partial delivery qty adds up to ${scheduleTotal(l)} but the line qty is ${num(l.qty)}`;
  }
  return "";
};

export function PartialDeliveryEditor({ line, onChange, testid = "partial" }) {
  const lots = line.delivery_schedule || [];
  const total = scheduleTotal(line);
  const remaining = round(num(line.qty) - total);
  const setLots = (next) => onChange({ delivery_schedule: next });

  const toggle = (on) => onChange(on
    ? { partial_delivery: true, delivery_schedule: lots.length ? lots : [{ delivery_date: "", qty: num(line.qty) }] }
    : { partial_delivery: false });

  return (
    <div className="mt-2" data-testid={testid}>
      <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
        <Checkbox checked={!!line.partial_delivery} onCheckedChange={(v) => toggle(!!v)} data-testid={`${testid}-toggle`} />
        <CalendarClock className="w-3.5 h-3.5 text-slate-500" /> Partial Delivery
      </label>
      {line.partial_delivery && (
        <div className="mt-2 ml-6 border border-slate-200 rounded-md bg-white p-2 max-w-xl">
          <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wide text-slate-500 px-1">
            <div className="col-span-1">#</div>
            <div className="col-span-6">Required delivery date</div>
            <div className="col-span-4">Qty</div>
          </div>
          {lots.map((e, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center mt-1">
              <div className="col-span-1 text-xs text-slate-500 mono pl-1">{i + 1}</div>
              <div className="col-span-6">
                <Input type="date" value={e.delivery_date || ""} className="h-8"
                  onChange={(ev) => setLots(lots.map((x, j) => (j === i ? { ...x, delivery_date: ev.target.value } : x)))}
                  data-testid={`${testid}-date-${i}`} />
              </div>
              <div className="col-span-4">
                <Input type="number" min={0} value={e.qty} className="h-8"
                  onChange={(ev) => setLots(lots.map((x, j) => (j === i ? { ...x, qty: ev.target.value } : x)))}
                  data-testid={`${testid}-qty-${i}`} />
              </div>
              <div className="col-span-1">
                <Button variant="ghost" size="icon" className="h-8 w-8" disabled={lots.length === 1}
                  onClick={() => setLots(lots.filter((_, j) => j !== i))} data-testid={`${testid}-remove-${i}`}>
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between mt-2 px-1">
            <Button variant="outline" size="sm" className="h-7"
              onClick={() => setLots([...lots, { delivery_date: "", qty: remaining > 0 ? remaining : 0 }])}
              data-testid={`${testid}-add`}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add delivery
            </Button>
            <span className={`text-[11px] mono ${Math.abs(remaining) > 1e-6 ? "text-red-600 font-semibold" : "text-emerald-700"}`}
              data-testid={`${testid}-total`}>
              {total} of {num(line.qty)} scheduled{Math.abs(remaining) > 1e-6 ? ` · ${remaining > 0 ? `${remaining} left` : `${-remaining} over`}` : " ✓"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Read-only lots under a line in a detail view.
export function PartialDeliveryList({ line }) {
  if (!line?.partial_delivery || !(line.delivery_schedule || []).length) return null;
  return (
    <div className="text-[11px] text-slate-600 mt-0.5" data-testid="partial-delivery-list">
      Partial delivery:{" "}
      {line.delivery_schedule.map((e, i) => (
        <span key={i} className="mono mr-2">
          {e.delivery_date ? new Date(e.delivery_date).toLocaleDateString() : "—"} × {e.qty}
        </span>
      ))}
    </div>
  );
}
